import React, { useState } from 'react'
import style from './Subproducts.module.scss'
import { IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { useFamilyAxes } from '../../hooks/useFamilyAxes'
import { LEGACY_AXIS } from '../../utils/familyAxes'
import { hiddenValueClass } from '../../utils/hiddenClass'

interface ISubproductsProps {
  compact?: boolean
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  onSubitemSelect: (index: number) => void
}

// AYTM-12710: multi-axis selector for Walmart; `compact` is the parent grid-card inline tile row
// (one value-only row per axis) — see specs/questions/ecommerce/product-family.md
// §Walmart Inline Variant Tiles.
const Subproducts = ({ schema, answer, onSubitemSelect, compact = false }: ISubproductsProps) => {
  const viewport = useViewport()
  const { languageTag } = useSurveyLocale()
  const { childrenProducts, axes, currentSelections, findSKUForValue, isCompatibleWithUpperAxes, isHiddenInPreview } =
    useFamilyAxes({
      answer,
      schema
    })

  // Per-axis hovered value for the dimension-value label (Walmart updates the VALUE on hover,
  // not the axis NAME — see spec §Variant Selector §Interactions).
  const [hoveredByAxis, setHoveredByAxis] = useState<Record<string, string>>({})

  if (childrenProducts.length <= 1 || axes.length === 0) return null

  function selectValue(axisName: string, value: string) {
    const targetSKU = findSKUForValue(axisName, value)
    if (!targetSKU) return
    const answerIndex = schema.answers.indexOf(targetSKU)
    if (answerIndex >= 0) onSubitemSelect(answerIndex)
  }

  return (
    <>
      {axes.map((axis, axisIndex) => {
        const selectedValue = currentSelections[axis.name] || ''
        const hoveredValue = hoveredByAxis[axis.name] ?? selectedValue
        // Spec §Multi-Axis Selector Pattern §Last-axis vs upper-axes: price / per-count
        // metadata renders only on the last (innermost) axis row.
        const isLastAxis = axisIndex === axes.length - 1
        return (
          <div
            key={axis.name}
            className={`${style.amount} ${viewport.mobile && !compact ? style.amountMobile : ''} ${
              compact ? style.compact : ''
            }`}
          >
            {!compact && axis.name !== LEGACY_AXIS ? (
              <p>
                <strong>{axis.name}:</strong>&nbsp;{hoveredValue}
              </p>
            ) : null}
            <div className={style.items}>
              {axis.values.map(value => {
                // Walmart hides incompatible values entirely on lower axes; axis 1 always renders every value,
                // and a preview-hidden value renders regardless, washed out. Spec §Multi-Axis Selector Pattern.
                const hiddenInPreview = isHiddenInPreview(axis.name, value)
                if (!hiddenInPreview && !isCompatibleWithUpperAxes(axisIndex, value)) return null

                const matchSKU = findSKUForValue(axis.name, value)
                const isSelected = selectedValue === value
                const regularPrice = matchSKU?.price
                const discountedPrice = matchSKU?.discounted_price
                const hasDiscount = Boolean(discountedPrice) && Boolean(regularPrice) && discountedPrice < regularPrice
                return (
                  <button
                    key={value}
                    aria-pressed={isSelected}
                    onClick={() => selectValue(axis.name, value)}
                    onMouseEnter={
                      compact ? undefined : () => setHoveredByAxis(prev => ({ ...prev, [axis.name]: value }))
                    }
                    onMouseLeave={
                      compact ? undefined : () => setHoveredByAxis(prev => ({ ...prev, [axis.name]: selectedValue }))
                    }
                    className={`${style.select} ${isSelected ? style.selectActive : ''} ${hiddenValueClass(hiddenInPreview)}`}
                  >
                    <span className={style.itemAmount}>{value}</span>
                    {!compact && isLastAxis && matchSKU ? (
                      <>
                        <div
                          className={`${style.itemPrice} ${hasDiscount ? style.itemPriceDiscounted : ''}`}
                          dangerouslySetInnerHTML={{
                            __html: formatPriceWithLocale(
                              hasDiscount ? discountedPrice! : regularPrice!,
                              schema.price_mask,
                              languageTag,
                              true,
                              false
                            )
                          }}
                        />
                        {hasDiscount ? (
                          <div
                            className={style.itemPriceStrike}
                            dangerouslySetInnerHTML={{
                              __html: formatPriceWithLocale(regularPrice!, schema.price_mask, languageTag, true, false)
                            }}
                          />
                        ) : null}
                      </>
                    ) : null}
                    {!compact && isLastAxis && matchSKU?.per_count_price ? (
                      <div className={style.perCount}>{matchSKU.per_count_price}</div>
                    ) : null}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
    </>
  )
}

export default Subproducts
